import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Container, Fade } from '@mui/material';

// ─── i18n ─────────────────────────────────────────────────────────────────────

const TRANSLATIONS = {
  en: {
    thankYou: 'Thanks for completing the evaluation!',
    eyebrow: 'YOUR RESULT',
    points: 'POINTS',
    subtitle: (n) => (n === 1 ? 'Based on your response' : `Based on your ${n} responses`),
    noResultTitle: 'Result not defined',
    noResultNote: 'There is no interpretation configured for this score yet.',
    breakdownTitle: 'Response breakdown',
    questionLabel: (n) => `Question ${n}`,
    commentTitle: 'Your comment',
    contact: 'Have questions? Contact us anytime',
    createdBy: 'Created with 💜 by',
  },
  es: {
    thankYou: '¡Gracias por completar la evaluación!',
    eyebrow: 'TU RESULTADO',
    points: 'PUNTOS',
    subtitle: (n) => (n === 1 ? 'Basado en tu respuesta' : `Basado en tus ${n} respuestas`),
    noResultTitle: 'Resultado no definido',
    noResultNote: 'Todavía no hay una interpretación configurada para este puntaje.',
    breakdownTitle: 'Desglose de respuestas',
    questionLabel: (n) => `Pregunta ${n}`,
    commentTitle: 'Tu comentario',
    contact: '¿Tienes preguntas? Contáctanos cuando quieras',
    createdBy: 'Creado con 💜 por',
  },
};

/**
 * Detecta el idioma del navegador. Regresa 'es' para hispanohablantes,
 * 'en' para todos los demás.
 */
const detectLocale = () => {
  const langs = navigator.languages?.length ? navigator.languages : [navigator.language || 'en'];
  for (const lang of langs) {
    if (lang.toLowerCase().startsWith('es')) return 'es';
    if (lang.toLowerCase().startsWith('en')) return 'en';
  }
  return 'en';
};

// ─── Paleta ───────────────────────────────────────────────────────────────────
// Cálida (durazno/crema) + acento morado — deliberadamente distinta del combo
// "crema + terracota" que se ve en todos lados; el morado es lo único audaz.

const PALETTE = {
  bgTop: '#FCEFDD',
  bgBottom: '#F3DEC2',
  ring: '#6C3FA6',
  ringTrack: 'rgba(108,63,166,0.16)',
  ink: '#2C2233',
  inkSoft: '#8A7A6B',
  card: '#FFFFFF',
  cardBorder: 'rgba(108,63,166,0.14)',
  pillBg: 'rgba(108,63,166,0.10)',
  barTrack: 'rgba(108,63,166,0.12)',
};

// Paleta rotativa para el punto de color del badge — así cada etiqueta de
// resultado (el `label` de la regla que ganó) tiene un color estable propio,
// sin inventar semántica (bueno/malo) que no viene en los datos.
const DOT_COLORS = ['#6C3FA6', '#B15A82', '#2E86AB', '#3F9D6F', '#C97A2B'];
const colorForLabel = (label = '') => {
  let hash = 0;
  for (let i = 0; i < label.length; i += 1) hash = label.charCodeAt(i) + ((hash << 5) - hash);
  return DOT_COLORS[Math.abs(hash) % DOT_COLORS.length];
};

// ─── Cálculo del resultado ─────────────────────────────────────────────────────
// El score es la SUMA de cada `ans` presente en docData.answer (q1, q2, ...).

const computeScore = (docData) => {
  const answerMap = docData?.answer || {};
  return Object.values(answerMap).reduce((total, entry) => {
    const parsed = parseInt(entry?.ans, 10);
    return total + (Number.isNaN(parsed) ? 0 : parsed);
  }, 0);
};

const SIMPLE_OPERATORS = {
  '=': (score, value) => score === value,
  '==': (score, value) => score === value,
  '!=': (score, value) => score !== value,
  '>': (score, value) => score > value,
  '>=': (score, value) => score >= value,
  '<': (score, value) => score < value,
  '<=': (score, value) => score <= value,
};

/**
 * Evalúa una regla contra el score. Soporta los operadores simples
 * (=, !=, >, >=, <, <=) y 'between', que compara contra el rango inclusivo
 * formado por rule.value ("Desde") y rule.valueTo ("Hasta"), en cualquier orden.
 */
const ruleMatchesScore = (rule, score) => {
  if (rule.operator === 'between') {
    if (rule.valueTo === '' || rule.valueTo === undefined || rule.valueTo === null) return false;
    const from = typeof rule.value === 'number' ? rule.value : parseFloat(rule.value);
    const to = typeof rule.valueTo === 'number' ? rule.valueTo : parseFloat(rule.valueTo);
    if (Number.isNaN(from) || Number.isNaN(to)) return false;
    return score >= Math.min(from, to) && score <= Math.max(from, to);
  }
  const compare = SIMPLE_OPERATORS[rule.operator];
  if (!compare) return false;
  const ruleValue = typeof rule.value === 'number' ? rule.value : parseFloat(rule.value);
  if (Number.isNaN(ruleValue)) return false;
  return compare(score, ruleValue);
};

const matchResultRule = (score, resultRules) => {
  if (!Array.isArray(resultRules)) return null;
  return resultRules.find((rule) => ruleMatchesScore(rule, score)) || null;
};

/**
 * El anillo necesita un "máximo" para saber qué tan lleno dibujarse. No viene
 * declarado en ningún lado, así que lo inferimos del rango más alto que
 * cualquier regla usa (value / valueTo). Si ninguna regla trae un número
 * utilizable, no inventamos una escala: el anillo se muestra lleno para
 * resaltar el puntaje tal cual.
 */
const getScaleMax = (resultRules) => {
  if (!Array.isArray(resultRules)) return null;
  let max = null;
  resultRules.forEach((r) => {
    [r.value, r.valueTo].forEach((v) => {
      const n = typeof v === 'number' ? v : parseFloat(v);
      if (!Number.isNaN(n) && (max === null || n > max)) max = n;
    });
  });
  return max;
};

// ─── Anillo de puntaje (SVG) ───────────────────────────────────────────────────

const RING_SIZE = 240;
const RING_STROKE = 20;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const RING_GAP_DEG = 42; // abertura visible arriba, como un medidor
const RING_USABLE_LEN = RING_CIRCUMFERENCE * ((360 - RING_GAP_DEG) / 360);
const RING_ROTATION = -90 + RING_GAP_DEG / 2;

const ScoreRing = ({ score, percentage, pointsLabel, reduceMotion }) => {
  const [grown, setGrown] = useState(reduceMotion);

  useEffect(() => {
    if (reduceMotion) return undefined;
    const id = setTimeout(() => setGrown(true), 150);
    return () => clearTimeout(id);
  }, [reduceMotion]);

  const progressLen = RING_USABLE_LEN * Math.min(Math.max(percentage, 0), 1);

  return (
    <Box sx={{ position: 'relative', width: { xs: 190, sm: 220 }, height: { xs: 190, sm: 220 }, mx: 'auto' }}>
      <svg width="100%" height="100%" viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}>
        <g transform={`rotate(${RING_ROTATION} ${RING_SIZE / 2} ${RING_SIZE / 2})`}>
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            fill="none"
            stroke={PALETTE.ringTrack}
            strokeWidth={RING_STROKE}
            strokeLinecap="round"
            strokeDasharray={`${RING_USABLE_LEN} ${RING_CIRCUMFERENCE}`}
          />
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            fill="none"
            stroke={PALETTE.ring}
            strokeWidth={RING_STROKE}
            strokeLinecap="round"
            strokeDasharray={`${grown ? progressLen : 0} ${RING_CIRCUMFERENCE}`}
            style={{ transition: reduceMotion ? 'none' : 'stroke-dasharray 1.1s cubic-bezier(.22,1,.36,1)' }}
          />
        </g>
      </svg>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontWeight: 700,
            fontSize: { xs: '2.6rem', sm: '3rem' },
            lineHeight: 1,
            color: PALETTE.ink,
          }}
        >
          {score}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: 2, color: PALETTE.inkSoft, mt: 0.5 }}>
          {pointsLabel}
        </Typography>
      </Box>
    </Box>
  );
};

// ─── Badge de resultado ────────────────────────────────────────────────────────

const ResultBadge = ({ label, dotColor }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      px: 2.2,
      py: 0.9,
      borderRadius: 999,
      background: PALETTE.pillBg,
    }}
  >
    <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: PALETTE.ink }}>{label}</Typography>
  </Box>
);

// ─── Fila de una respuesta en el desglose ──────────────────────────────────────

const AnswerRow = ({ orden, questionLabel, isNumeric, ans, barPct }) => (
  <Box sx={{ py: 2, '&:not(:last-of-type)': { borderBottom: `1px solid ${PALETTE.cardBorder}` } }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            background: PALETTE.pillBg,
            color: PALETTE.ring,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.75rem',
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {orden}
        </Box>
        <Typography sx={{ fontWeight: 600, color: PALETTE.ink, fontSize: '0.95rem' }}>
          {questionLabel}
        </Typography>
      </Box>
      {isNumeric ? (
        <Typography sx={{ fontWeight: 700, color: PALETTE.ring, fontSize: '0.95rem', flexShrink: 0 }}>
          {ans}
        </Typography>
      ) : (
        <Typography
          sx={{
            color: PALETTE.inkSoft,
            fontSize: '0.85rem',
            fontStyle: 'italic',
            maxWidth: 180,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          “{String(ans)}”
        </Typography>
      )}
    </Box>
    {isNumeric && (
      <Box sx={{ mt: 1.2, height: 8, borderRadius: 999, background: PALETTE.barTrack, overflow: 'hidden' }}>
        <Box sx={{ width: `${barPct}%`, height: '100%', borderRadius: 999, background: PALETTE.ring }} />
      </Box>
    )}
  </Box>
);

// ─── Componente principal ──────────────────────────────────────────────────────

/**
 * @param {object} docData     Documento de la respuesta enviada, p. ej.
 *                              { answer: { q1: { ans, orden }, ... }, comm, ... }
 * @param {Array}  resultRules Reglas { id, label, operator, value, valueTo, detalles }
 *                              usadas para interpretar el puntaje total (suma de
 *                              todos los `ans`). Se evalúan en orden; gana la primera
 *                              que haga match. 'between' compara contra el rango
 *                              inclusivo value↔valueTo. Si ninguna hace match se
 *                              muestra un estado neutro de "resultado no definido".
 *
 * El diseño solo usa datos que existen realmente en docData/resultRules: no asume
 * nada sobre el tema de la evaluación (riesgo, salud, satisfacción, etc.), así que
 * funciona igual para cualquier encuesta.
 */
const ThanksUserResult = ({ docData, resultRules }) => {
  const t = useMemo(() => TRANSLATIONS[detectLocale()], []);
  const reduceMotion = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
    []
  );

  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(id);
  }, []);

  const score = useMemo(() => computeScore(docData), [docData]);
  const matchedRule = useMemo(() => matchResultRule(score, resultRules), [score, resultRules]);
  const hasResult = !!matchedRule;
  const hasDetails = hasResult && !!matchedRule.detalles?.trim();

  const scaleMax = useMemo(() => getScaleMax(resultRules), [resultRules]);
  const percentage = useMemo(() => {
    if (scaleMax && scaleMax > 0) return Math.min(Math.max(score / scaleMax, 0), 1);
    return 1; // sin escala conocida: resaltamos el anillo completo
  }, [score, scaleMax]);

  const breakdown = useMemo(() => {
    const answerMap = docData?.answer || {};
    const rows = Object.values(answerMap)
      .map((e) => ({ orden: e?.orden ?? 0, ans: e?.ans }))
      .sort((a, b) => a.orden - b.orden);

    const numericValues = rows
      .map((r) => parseFloat(r.ans))
      .filter((n) => !Number.isNaN(n));
    const maxNumeric = numericValues.length ? Math.max(...numericValues) : 0;

    return rows.map((r) => {
      const n = parseFloat(r.ans);
      const isNumeric = r.ans !== '' && r.ans !== null && r.ans !== undefined && !Number.isNaN(n);
      return {
        ...r,
        isNumeric,
        barPct: isNumeric && maxNumeric > 0 ? Math.max((n / maxNumeric) * 100, 6) : 0,
      };
    });
  }, [docData]);

  const comment = docData?.comm?.trim?.();
  const badgeLabel = hasResult ? matchedRule.label : t.noResultTitle;
  const badgeColor = hasResult ? colorForLabel(matchedRule.label) : '#9C8F82';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        background: `linear-gradient(160deg, ${PALETTE.bgTop} 0%, ${PALETTE.bgBottom} 100%)`,
        py: { xs: 6, sm: 9 },
        px: 2,
      }}
    >
      {/* Fuente decorativa para el número del puntaje; se degrada a Georgia si no carga */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,700&display=swap');`}</style>

      <Container maxWidth="sm">
        <Fade in={visible} timeout={500}>
          <Box>
            {/* ── Cabecera: anillo + badge + subtítulo ───────────────────── */}
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <Typography
                sx={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: 3, color: PALETTE.inkSoft, mb: 1 }}
              >
                {t.eyebrow}
              </Typography>

              <ScoreRing score={score} percentage={percentage} pointsLabel={t.points} reduceMotion={reduceMotion} />

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                <ResultBadge label={badgeLabel} dotColor={badgeColor} />
              </Box>

              <Typography sx={{ color: PALETTE.inkSoft, mt: 2, fontSize: '0.95rem' }}>
                {t.subtitle(breakdown.length)}
              </Typography>

              <Typography sx={{ color: PALETTE.ink, mt: 1, fontWeight: 600 }}>{t.thankYou}</Typography>
            </Box>

            {/* ── Detalles de la regla (si la encuesta los definió) ──────── */}
            {hasDetails && (
              <Box
                sx={{
                  background: PALETTE.card,
                  border: `1px solid ${PALETTE.cardBorder}`,
                  borderRadius: 3,
                  p: { xs: 2.5, sm: 3 },
                  mb: 4,
                }}
              >
                <Typography sx={{ color: PALETTE.ink, lineHeight: 1.8, whiteSpace: 'pre-line', fontSize: '0.95rem' }}>
                  {matchedRule.detalles}
                </Typography>
              </Box>
            )}
            {!hasResult && (
              <Typography sx={{ textAlign: 'center', color: PALETTE.inkSoft, fontSize: '0.85rem', mb: 4 }}>
                {t.noResultNote}
              </Typography>
            )}

      

            {/* ── Comentario libre, si lo dejó la persona ─────────────────── */}
            {comment && (
              <Box
                sx={{
                  background: PALETTE.card,
                  border: `1px solid ${PALETTE.cardBorder}`,
                  borderRadius: 3,
                  p: { xs: 2.5, sm: 3 },
                }}
              >
                <Typography sx={{ fontWeight: 700, color: PALETTE.ink, mb: 1, fontSize: '0.95rem' }}>
                  {t.commentTitle}
                </Typography>
                <Typography sx={{ color: PALETTE.inkSoft, lineHeight: 1.7, fontSize: '0.9rem' }}>
                  {comment}
                </Typography>
              </Box>
            )}

            {/* ── Footer ───────────────────────────────────────────────────── */}
            <Box sx={{ textAlign: 'center', mt: 6 }}>
              <Typography sx={{ color: PALETTE.inkSoft, fontSize: '0.85rem' }}>
                {t.contact}
              </Typography>
              <Typography sx={{ color: PALETTE.inkSoft, fontSize: '0.8rem', mt: 0.5 }}>
                {t.createdBy}{' '}
                <a href="https://emojisurveys.pro" target="_blank" rel="noopener noreferrer" style={{ color: PALETTE.ring, fontWeight: 700, textDecoration: 'none' }}>
                  emojisurveys.pro
                </a>
              </Typography>
            </Box>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default ThanksUserResult;